import { Box, Stack } from '@chakra-ui/react';
import React from 'react';
import Select, { StylesConfig } from 'react-select';

// Define the option type
interface Option {
  key: string;
  value: string;
}

// React-select expects options in a specific format
interface SelectOption {
  value: string;
  label: string;
}

// Props for our custom select component
interface HelivanSelectorProps {
  question?: string;
  options: Option[];
  selectedOption: any | null;
  onSelect: (key: any) => void;
  maxHeightOverride?: number;
  minHeightOverride?: number;
  lightMode?: boolean;
  selectWidth?: string;
  size?: 'sm' | 'lg';
  labelSize?: 'sm' | 'lg';
  menuPlacement?: 'auto' | 'bottom' | 'top';
  type?: 'primary' | 'secondary';
  defaultValue?: string;
}

export const HelivanSelector: React.FC<HelivanSelectorProps> = ({
  question,
  options,
  selectedOption,
  onSelect,
  maxHeightOverride,
  minHeightOverride,
  lightMode,
  selectWidth,
  size,
  menuPlacement,
  defaultValue,
  labelSize = 'lg',
  type = 'primary',
}) => {
  // Transform our options to the format react-select expects
  const selectOptions: SelectOption[] = options.map((option) => ({
    value: option.key,
    label: option.value,
  }));

  const maxHeight = maxHeightOverride ?? 130;

  // Find the currently selected option
  const selectedValue = selectedOption
    ? selectOptions.find((option) => option.value === selectedOption)
    : null;

  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'white' : '#f8fafc',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: state.isFocused ? '#00477D' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0,71,125,0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#00477D' : '#cbd5e1',
      },
      height: size === 'lg' ? '48px' : '40px',
      minHeight: size === 'lg' ? '48px' : '40px',
      fontSize: size === 'lg' ? '15px' : '14px',
      fontWeight: 400,
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(0,71,125,0.1)'
        : state.isFocused
          ? '#f8fafc'
          : 'white',
      color: state.isSelected ? '#00477D' : '#1a1a2e',
      padding: '10px 14px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: state.isSelected ? 600 : 400,
      fontSize: size === 'lg' ? '15px' : '14px',
      transition: 'all 0.15s',
      '&:active': {
        backgroundColor: 'rgba(0,71,125,0.15)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderRadius: '14px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      marginTop: '4px',
      zIndex: 100,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '6px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1a1a2e',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#94a3b8',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#00477D' : '#64748b',
      transition: 'all 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    input: (provided) => ({
      ...provided,
      color: '#1a1a2e',
    }),
  };

  // Handle change
  const handleChange = (selected: SelectOption | null) => {
    if (selected) {
      onSelect(selected.value);
    }
  };

  return (
    <Stack spacing={2} width="100%">
      {question && (
        <Box
          fontSize="13px"
          fontWeight={600}
          color={lightMode ? 'white' : '#475569'}
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {question}
        </Box>
      )}

      <Box width={selectWidth ?? '100%'}>
        <Select
          defaultValue={
            defaultValue
              ? selectOptions.find((option) => option.value === defaultValue)
              : undefined
          }
          options={selectOptions}
          value={selectedValue}
          onChange={handleChange}
          styles={customStyles}
          placeholder=""
          minMenuHeight={minHeightOverride}
          maxMenuHeight={maxHeight}
          isClearable={false}
          isSearchable={true}
          menuPlacement={menuPlacement ?? 'auto'}
        />
      </Box>
    </Stack>
  );
};
